import React, { useMemo, useState } from 'react';
import axios from '@mysimpledb/shared/config/axios';
import PhoneNumberField, {
  normalizePhoneForSave,
} from '@mysimpledb/shared/components/PhoneNumberField/PhoneNumberField';
import AppButton from '@mysimpledb/shared/components/UI/Button/Button';
import SuccessAccount from './SuccessAccount';
import '../LoginForm.css';
import './CreateAccount.css';
import { Link } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus } from '@fortawesome/free-solid-svg-icons';

const DEFAULT_PHONE_COUNTRY = 'US';

// This is the endpoint used by the old CreateAccount flow.
// It creates the user and sends the verification email.
const REGISTER_ENDPOINT = '/auth/register';
const RESEND_VERIFICATION_ENDPOINT = '/auth/resend-verification';
const VALIDATE_PHONE_ENDPOINT = '/twilio/validate-phone';
const SEND_PHONE_VERIFICATION_ENDPOINT = '/twilio/phone-verification/send';
const CHECK_PHONE_VERIFICATION_ENDPOINT = '/twilio/phone-verification/check';

// Old flow used /auth/email-available. If you add /users/check-email instead,
// this function will support either route.
const EMAIL_AVAILABLE_ENDPOINTS = [
  '/auth/email-available',
  '/users/check-email',
];

const fieldStyle = { marginBottom: 8 };
const phoneFieldStyle = { marginTop: 4, marginBottom: 10 };
const passwordFieldStyle = { marginBottom: 10 };
const confirmPasswordFieldStyle = { marginTop: 8, marginBottom: 8 };

function getSmsStatus({ rawPhone, normalizedPhone, smsOptIn }) {
  const hasTypedPhone = String(rawPhone || '').trim().length > 0;

  if (smsOptIn && normalizedPhone) return 'enabled';
  if (smsOptIn && !normalizedPhone)
    return hasTypedPhone ? 'invalid_phone' : 'missing_phone';
  if (!smsOptIn && normalizedPhone) return 'not_enabled_no_consent';

  return 'not_enabled';
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email || '').trim());
}

async function checkEmailAvailable(email) {
  const normalizedEmail = String(email || '')
    .trim()
    .toLowerCase();

  for (const endpoint of EMAIL_AVAILABLE_ENDPOINTS) {
    try {
      const resp = await axios.get(endpoint, {
        params: { email: normalizedEmail },
      });

      return {
        ok: true,
        available: Boolean(resp?.data?.available),
        endpoint,
      };
    } catch (err) {
      // If this endpoint does not exist, try the next one.
      if (err?.response?.status === 404) {
        continue;
      }

      // For non-404 errors, soft-fail. Submit still has server/database protection.
      return {
        ok: false,
        available: true,
        error: err,
      };
    }
  }

  // No availability endpoint is available. Do not block account creation.
  return {
    ok: false,
    available: true,
    skipped: true,
  };
}

const CreateUserForm = ({ onGoToLogin }) => {
  const [form, setForm] = useState({
    fname: '',
    lname: '',
    email: '',
    phone: '',
    phoneCountry: DEFAULT_PHONE_COUNTRY,
    password: '',
    smsOptIn: false,
    termsAccepted: false,
  });

  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [emailChecking, setEmailChecking] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [emailStatus, setEmailStatus] = useState('');
  const [lastCheckedEmail, setLastCheckedEmail] = useState('');

  const [createdEmail, setCreatedEmail] = useState('');
  const [devVerificationLink, setDevVerificationLink] = useState(null);

  const [smsStatus, setSmsStatus] = useState('not_enabled');

  const [phoneVerificationToken, setPhoneVerificationToken] = useState('');
  const [phoneValidationLoading, setPhoneValidationLoading] = useState(false);
  const [phoneVerificationLoading, setPhoneVerificationLoading] =
    useState(false);
  const [phoneVerificationSent, setPhoneVerificationSent] = useState(false);
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verifiedPhoneE164, setVerifiedPhoneE164] = useState('');
  const [phoneVerificationError, setPhoneVerificationError] = useState('');
  const [phoneVerificationMessage, setPhoneVerificationMessage] = useState('');

  const passwordRules = useMemo(
    () => ({
      minLength: form.password.length >= 8,
      upper: /[A-Z]/.test(form.password),
      lower: /[a-z]/.test(form.password),
      number: /\d/.test(form.password),
      special: /[@$!%*?&]/.test(form.password),
    }),
    [form.password]
  );

  const isSecurePassword = Object.values(passwordRules).every(Boolean);
  const passwordsMatch =
    form.password.length > 0 && confirmPassword.length > 0
      ? form.password === confirmPassword
      : true;

  const normalizedEmail = form.email.trim().toLowerCase();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (name === 'email') {
      setEmailAvailable(null);
      setEmailStatus('');
      setLastCheckedEmail('');
    }
  };

  const handleEmailBlur = async () => {
    const email = form.email.trim().toLowerCase();

    setEmailStatus('');
    setEmailAvailable(null);

    if (!email) return;

    if (!isValidEmail(email)) {
      setEmailAvailable(false);
      setEmailStatus('Please enter a valid email address.');
      return;
    }

    setEmailChecking(true);

    try {
      const availability = await checkEmailAvailable(email);
      setLastCheckedEmail(email);

      if (availability.ok && availability.available === false) {
        setEmailAvailable(false);
        setEmailStatus(
          'That email is already registered. Please use a different email or log in.'
        );
        return;
      }

      if (availability.ok && availability.available === true) {
        setEmailAvailable(true);
        setEmailStatus('');
        return;
      }

      // If the availability route is unavailable, do not block the user here.
      setEmailAvailable(null);
      setEmailStatus('');
    } finally {
      setEmailChecking(false);
    }
  };

  const resetPhoneVerification = () => {
    setPhoneVerificationSent(false);
    setPhoneVerificationCode('');
    setPhoneVerified(false);
    setVerifiedPhoneE164('');
    setPhoneVerificationToken('');
    setPhoneVerificationError('');
    setPhoneVerificationMessage('');
  };

  const handlePhoneChange = (value) => {
    setForm((prev) => ({
      ...prev,
      phone: value,
    }));

    resetPhoneVerification();
  };

  const handlePhoneCountryChange = (value) => {
    setForm((prev) => ({
      ...prev,
      phoneCountry: value,
    }));

    resetPhoneVerification();
  };

  const handleSmsOptInChange = (event) => {
    const checked = event.target.checked;

    setForm((prev) => ({
      ...prev,
      smsOptIn: checked,
    }));

    if (!checked) {
      resetPhoneVerification();
    } else {
      setPhoneVerificationError('');
      setPhoneVerificationMessage('');
    }
  };

  const sendPhoneVerification = async () => {
    setPhoneValidationLoading(true);
    setPhoneVerificationError('');
    setPhoneVerificationMessage('');

    try {
      const normalizedPhone = normalizePhoneForSave(
        form.phone,
        form.phoneCountry || null
      );

      if (!normalizedPhone) {
        setPhoneVerificationError(
          'Please enter a valid phone number before requesting a code.'
        );
        return;
      }

      const validationResponse = await axios.post(VALIDATE_PHONE_ENDPOINT, {
        phoneE164: normalizedPhone,
      });

      if (!validationResponse?.data?.valid) {
        setPhoneVerificationError('Please enter a valid phone number.');
        return;
      }

      const phoneE164 = validationResponse.data.phoneE164 || normalizedPhone;

      await axios.post(SEND_PHONE_VERIFICATION_ENDPOINT, {
        phoneE164,
      });

      setVerifiedPhoneE164(phoneE164);
      setPhoneVerificationSent(true);
      setPhoneVerified(false);
      setPhoneVerificationCode('');
      setPhoneVerificationMessage(
        `A verification code was sent to ${
          validationResponse.data.nationalFormat || phoneE164
        }.`
      );
    } catch (error) {
      setPhoneVerificationError(
        error?.response?.data?.message ||
          'Unable to send the verification code. Please try again.'
      );
    } finally {
      setPhoneValidationLoading(false);
    }
  };

  const verifyPhoneCode = async () => {
    setPhoneVerificationLoading(true);
    setPhoneVerificationError('');
    setPhoneVerificationMessage('');

    try {
      const code = phoneVerificationCode.trim();

      if (!/^\d{4,10}$/.test(code)) {
        setPhoneVerificationError('Please enter the verification code.');
        return;
      }

      if (!verifiedPhoneE164) {
        setPhoneVerificationError('Please request a new verification code.');
        return;
      }

      const response = await axios.post(CHECK_PHONE_VERIFICATION_ENDPOINT, {
        phoneE164: verifiedPhoneE164,
        code,
      });

      if (!response?.data?.verified) {
        setPhoneVerificationError(
          'The verification code is incorrect or has expired.'
        );
        return;
      }

      const returnedToken = response?.data?.phoneVerificationToken;

      const returnedPhone = response?.data?.phoneE164 || verifiedPhoneE164;

      if (!returnedToken) {
        setPhoneVerified(false);
        setPhoneVerificationToken('');
        setPhoneVerificationError(
          'The server did not return phone verification proof. Please try again.'
        );
        return;
      }

      setVerifiedPhoneE164(returnedPhone);
      setPhoneVerificationToken(returnedToken);
      setPhoneVerified(true);
      setPhoneVerificationMessage('Phone number verified.');
    } catch (error) {
      setPhoneVerified(false);
      setPhoneVerificationToken('');

      setPhoneVerificationError(
        error?.response?.data?.message ||
          'Unable to verify the phone number. Please try again.'
      );
    } finally {
      setPhoneVerificationLoading(false);
    }
  };

  const validateForm = () => {
    if (!form.fname.trim()) return 'Please enter your first name.';
    if (!form.lname.trim()) return 'Please enter your last name.';

    if (!isValidEmail(form.email)) {
      return 'Please enter a valid email address.';
    }

    if (emailAvailable === false && lastCheckedEmail === normalizedEmail) {
      return 'That email is already in use. Please use a different email or log in.';
    }

    // Phone remains optional unless the user enables SMS notifications.
    if (form.smsOptIn && (!phoneVerified || !phoneVerificationToken)) {
      return 'Please verify your phone number before enabling SMS notifications.';
    }

    if (!isSecurePassword) {
      return 'Password must be 8+ characters and include uppercase, lowercase, number, and special character.';
    }

    if (form.password !== confirmPassword) {
      return 'Passwords do not match.';
    }

    if (!form.termsAccepted) {
      return 'Please agree to the Terms of Service and Privacy Policy.';
    }

    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    const validationError = validateForm();
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      // Re-check email immediately before submitting if it has not already been
      // checked for the current email value.
      if (lastCheckedEmail !== normalizedEmail || emailAvailable === null) {
        const availability = await checkEmailAvailable(normalizedEmail);
        setLastCheckedEmail(normalizedEmail);

        if (availability.ok && availability.available === false) {
          setEmailAvailable(false);
          setEmailStatus(
            'That email is already in use. Please use a different email or log in.'
          );
          setErrorMsg(
            'That email is already in use. Please use a different email or log in.'
          );
          return;
        }

        if (availability.ok && availability.available === true) {
          setEmailAvailable(true);
          setEmailStatus('');
        }
      }

      const normalizedPhone = normalizePhoneForSave(
        form.phone,
        form.phoneCountry || null
      );

      let nextSmsStatus = getSmsStatus({
        rawPhone: form.phone,
        normalizedPhone,
        smsOptIn: form.smsOptIn,
      });

      const phoneForSubmit =
        form.smsOptIn && phoneVerified ? verifiedPhoneE164 : normalizedPhone;

      const payload = {
        // Include both naming styles so the updated form works with either
        // the newer /users-style controller or the old /auth/register flow.
        fname: form.fname.trim(),
        lname: form.lname.trim(),
        firstName: form.fname.trim(),
        lastName: form.lname.trim(),

        email: normalizedEmail,
        phone: phoneForSubmit,
        phoneCountry: phoneForSubmit
          ? form.phoneCountry.trim().toUpperCase()
          : null,
        password: form.password,
        smsOptIn: Boolean(
          form.smsOptIn &&
            phoneVerified &&
            phoneForSubmit &&
            phoneVerificationToken
        ),
        phoneVerificationToken:
          form.smsOptIn && phoneVerified ? phoneVerificationToken : null,
        termsAccepted: Boolean(form.termsAccepted),
      };

      const res = await axios.post(REGISTER_ENDPOINT, payload);
      const beSmsStatus = res?.data?.smsStatus;

      if (beSmsStatus) {
        nextSmsStatus = beSmsStatus;
      }

      setSuccessMsg(
        'Account created. Please check your email for the verification link.'
      );

      setSmsStatus(nextSmsStatus);
      setCreatedEmail(normalizedEmail);
      setDevVerificationLink(res?.data?.devVerificationLink || null);
    } catch (err) {
      console.error('Error creating user:', err);

      const registerError = err?.response?.data?.error;

      const phoneVerificationErrors = [
        'phone_verification_required',
        'missing_phone_verification_token',
        'phone_verification_expired_or_used',
        'phone_verification_phone_mismatch',
        'invalid_phone_verification_proof',
        'invalid_phone',
        'phone_already_used',
      ];

      if (phoneVerificationErrors.includes(registerError)) {
        setPhoneVerified(false);
        setPhoneVerificationToken('');
        setPhoneVerificationSent(false);
        setPhoneVerificationCode('');

        const message =
          registerError === 'phone_verification_phone_mismatch'
            ? 'The phone number no longer matches the verified number. Please verify it again.'
            : registerError === 'invalid_phone'
            ? 'The phone number is invalid. Please correct it and verify it again.'
            : registerError === 'phone_already_used'
            ? 'That phone number is already associated with another MySimpleDB account. Please use a different phone number.'
            : 'Your phone verification has expired or is no longer valid. Please verify your phone again.';

        setPhoneVerificationError(message);
        setErrorMsg(message);
        return;
      }

      if (err?.response?.status === 409) {
        const duplicateMessage =
          'That email is already in use. Please use a different email or log in.';

        setEmailAvailable(false);
        setEmailStatus(duplicateMessage);
        setErrorMsg(duplicateMessage);
        return;
      }

      if (err?.response?.data?.error === 'invalid_email') {
        setErrorMsg('Please enter a valid email address.');
        return;
      }

      if (err?.response?.data?.error === 'password_too_short') {
        setErrorMsg('Password must be at least 8 characters.');
        return;
      }

      if (err?.response?.data?.error === 'missing_required_fields') {
        setErrorMsg('Please fill in all required fields.');
        return;
      }

      const serverMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Error creating account. Please try again.';

      setErrorMsg(serverMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitDisabled =
    isSubmitting ||
    emailChecking ||
    phoneValidationLoading ||
    phoneVerificationLoading ||
    !form.fname.trim() ||
    !form.lname.trim() ||
    !form.email.trim() ||
    !form.password ||
    !confirmPassword ||
    !isSecurePassword ||
    !passwordsMatch ||
    !form.termsAccepted ||
    (form.smsOptIn && (!phoneVerified || !phoneVerificationToken)) ||
    (emailAvailable === false && lastCheckedEmail === normalizedEmail);

  if (createdEmail) {
    return (
      <main className="create-account-page">
        <SuccessAccount
          email={createdEmail}
          smsStatus={smsStatus}
          devVerificationLink={devVerificationLink}
          onGoToLogin={
            onGoToLogin ||
            (() => {
              window.location.href = '/login';
            })
          }
          resendEndpoint={RESEND_VERIFICATION_ENDPOINT}
        />
      </main>
    );
  }

  return (
    <main className="create-account-page">
      <div className="create-account-card">
        <div className="create-account-header">
          <div className="create-account-icon">
            <FontAwesomeIcon icon={faUserPlus} />
          </div>

          <div className="create-account-eyebrow">Get started</div>

          <h1 className="create-account-title">Create your account</h1>

          <p className="create-account-subtitle">
            Set up your MySimpleDB account to securely organize and access your
            information from all your devices.
          </p>
        </div>

        {errorMsg && (
          <div className="create-account-error">
            <span>{errorMsg}</span>
          </div>
        )}

        {successMsg && (
          <div className="create-account-success">
            <span>{successMsg}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="create-account-form">
          <div className="login-field" style={fieldStyle}>
            <label htmlFor="fname" className="standard-label">
              First Name
            </label>
            <input
              type="text"
              name="fname"
              id="fname"
              className="standard-input"
              value={form.fname}
              onChange={handleChange}
              autoComplete="given-name"
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="login-field" style={fieldStyle}>
            <label htmlFor="lname" className="standard-label">
              Last Name
            </label>
            <input
              type="text"
              name="lname"
              id="lname"
              className="standard-input"
              value={form.lname}
              onChange={handleChange}
              autoComplete="family-name"
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="login-field" style={fieldStyle}>
            <label htmlFor="email" className="standard-label">
              Email
            </label>
            <input
              type="email"
              name="email"
              id="email"
              className="standard-input"
              value={form.email}
              onChange={handleChange}
              onBlur={handleEmailBlur}
              autoComplete="email"
              inputMode="email"
              disabled={isSubmitting}
              required
            />

            {emailChecking && (
              <div className="field-note">Checking email...</div>
            )}

            {!emailChecking && emailStatus && (
              <div
                className={
                  emailAvailable === false
                    ? 'email-error'
                    : 'field-note success'
                }
              >
                {emailStatus}
              </div>
            )}
          </div>

          <div className="login-field form-group phone" style={phoneFieldStyle}>
            <PhoneNumberField
              countryValue={form.phoneCountry || ''}
              onCountryChange={handlePhoneCountryChange}
              phoneValue={form.phone || ''}
              onPhoneChange={handlePhoneChange}
              disabled={
                isSubmitting ||
                phoneValidationLoading ||
                phoneVerificationLoading ||
                phoneVerified
              }
            />
          </div>

          <div className="login-field" style={passwordFieldStyle}>
            <label htmlFor="password" className="standard-label">
              Password
            </label>
            <div className="password-input-row">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                id="password"
                className="standard-input"
                value={form.password}
                onChange={handleChange}
                autoComplete="new-password"
                disabled={isSubmitting}
                required
              />

              <AppButton
                type="button"
                variant="solid"
                className="password-show-button"
                onPress={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? 'Hide' : 'Show'}
              </AppButton>
            </div>

            <ul className="pw-rules">
              <li className={passwordRules.minLength ? 'ok' : ''}>
                <span className="pw-icon">
                  {passwordRules.minLength ? '✓' : '◦'}
                </span>
                At least 8 characters
              </li>

              <li className={passwordRules.upper ? 'ok' : ''}>
                <span className="pw-icon">
                  {passwordRules.upper ? '✓' : '◦'}
                </span>
                One uppercase letter
              </li>

              <li className={passwordRules.lower ? 'ok' : ''}>
                <span className="pw-icon">
                  {passwordRules.lower ? '✓' : '◦'}
                </span>
                One lowercase letter
              </li>

              <li className={passwordRules.number ? 'ok' : ''}>
                <span className="pw-icon">
                  {passwordRules.number ? '✓' : '◦'}
                </span>
                One number
              </li>

              <li className={passwordRules.special ? 'ok' : ''}>
                <span className="pw-icon">
                  {passwordRules.special ? '✓' : '◦'}
                </span>
                One special character
              </li>
            </ul>
          </div>

          <div className="login-field" style={confirmPasswordFieldStyle}>
            <label htmlFor="confirmPassword" className="standard-label">
              Confirm Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              className="standard-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              disabled={isSubmitting}
              required
            />
            {!passwordsMatch && (
              <div className="small-text">Passwords do not match.</div>
            )}
          </div>

          <div className="section" style={{ marginTop: 12, paddingTop: 12 }}>
            <label className="checkbox-row">
              <input
                type="checkbox"
                name="smsOptIn"
                checked={form.smsOptIn}
                onChange={handleSmsOptInChange}
                disabled={
                  isSubmitting ||
                  phoneValidationLoading ||
                  phoneVerificationLoading
                }
              />
              <span>
                I agree to receive account-related text messages from
                MySimpleDB.
              </span>
            </label>
            <p className="muted">
              Messages may include account verification codes, password reset
              notifications, security alerts, reminders, sharing notifications,
              and synchronization updates. Message frequency varies. Message and
              data rates may apply. Reply STOP to opt out and HELP for
              assistance.
              <br />
              <br />
              By checking this box, you agree to the{' '}
              <Link to="/sms-terms" className="form-link">
                MySimpleDB SMS Terms
              </Link>
              .
            </p>
          </div>

          {form.smsOptIn && (
            <div
              className="phone-verification-panel"
              style={{
                marginTop: 12,
                padding: 14,
                border: '1px solid #dbe3ef',
                borderRadius: 10,
                background: '#f7f9fc',
              }}
            >
              {!phoneVerificationSent && !phoneVerified && (
                <AppButton
                  type="button"
                  variant="outline"
                  onPress={sendPhoneVerification}
                  disabled={
                    isSubmitting ||
                    phoneValidationLoading ||
                    phoneVerificationLoading
                  }
                >
                  {phoneValidationLoading
                    ? 'Sending code...'
                    : 'Verify phone number'}
                </AppButton>
              )}

              {phoneVerificationSent && !phoneVerified && (
                <>
                  <label
                    htmlFor="phoneVerificationCode"
                    className="standard-label"
                    style={{ marginTop: 0 }}
                  >
                    Verification Code
                  </label>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <input
                      type="text"
                      id="phoneVerificationCode"
                      className="standard-input"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={10}
                      value={phoneVerificationCode}
                      onChange={(event) =>
                        setPhoneVerificationCode(
                          event.target.value.replace(/\D/g, '').slice(0, 10)
                        )
                      }
                      disabled={isSubmitting || phoneVerificationLoading}
                      placeholder="Enter verification code"
                      style={{ flex: 1, minWidth: 0, margin: 0 }}
                    />

                    <AppButton
                      type="button"
                      variant="solid"
                      onPress={verifyPhoneCode}
                      disabled={
                        isSubmitting ||
                        phoneVerificationLoading ||
                        phoneVerificationCode.trim().length < 4
                      }
                    >
                      {phoneVerificationLoading
                        ? 'Verifying...'
                        : 'Verify code'}
                    </AppButton>
                  </div>

                  <button
                    type="button"
                    className="form-link"
                    onClick={sendPhoneVerification}
                    disabled={
                      isSubmitting ||
                      phoneValidationLoading ||
                      phoneVerificationLoading
                    }
                    style={{
                      marginTop: 10,
                      padding: 0,
                      border: 0,
                      background: 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    Send another code
                  </button>
                </>
              )}

              {phoneVerificationMessage &&
                !phoneVerificationError &&
                !phoneVerified && (
                  <div className="field-note" role="status">
                    {phoneVerificationMessage}
                  </div>
                )}

              {phoneVerified && (
                <div
                  className="field-note success"
                  role="status"
                  style={{ marginTop: 0 }}
                >
                  Phone number verified.
                </div>
              )}

              {phoneVerificationError && (
                <div className="email-error" role="alert">
                  {phoneVerificationError}
                </div>
              )}
            </div>
          )}

          <div className="section" style={{ marginTop: 10, paddingTop: 10 }}>
            <label className="checkbox-row">
              <input
                type="checkbox"
                name="termsAccepted"
                checked={form.termsAccepted}
                onChange={handleChange}
                disabled={isSubmitting}
                required
              />
              <span>
                I agree to the{' '}
                <Link to="/terms" className="form-link">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="form-link">
                  Privacy Policy
                </Link>
                .
              </span>
            </label>
          </div>

          <div className="actions">
            <AppButton
              type="submit"
              className="standard-button"
              disabled={submitDisabled}
            >
              {isSubmitting ? 'Creating...' : 'Create Account'}
            </AppButton>
          </div>
        </form>
      </div>
    </main>
  );
};

export default CreateUserForm;
